import APIService from "./APIService";
import pollForResult from "../../../utils/pollForResult";

/**
 * URL Safety Analysis API Service
 * Handles operations for URL safety analysis
 */
class URLSafetyAPI {
  /**
   * Submit a URL safety analysis request (returns immediately with analysisId).
   * The backend processes the AI analysis asynchronously to avoid AWS Gateway timeouts.
   * @param {string} url - The URL to analyze
   * @param {string|null} context - Optional additional context from the user
   * @returns {Promise<Object>} Promise that resolves to { analysisId, status }
   */
  static async submitAnalysis(url, context = null) {
    try {
      console.log("URLSafetyAPI: Submitting URL for analysis:", url);

      const payload = { url };
      if (context) {
        payload.context = context;
      }

      const response = await APIService.post(
        "/api/v1/scam/detector/url-safety/analyze",
        payload,
        { timeout: 15000 }, // 15s — just submitting, should be near-instant
      );

      console.log("URLSafetyAPI: Analysis submitted:", response);
      return response;
    } catch (error) {
      console.error("URLSafetyAPI: Error submitting URL analysis:", error);
      throw error;
    }
  }

  /**
   * Poll for the status/result of an async URL safety analysis.
   * @param {string} analysisId - The analysis ID returned by submitAnalysis
   * @returns {Promise<Object>} Promise that resolves to { status, result? }
   */
  static async checkAnalysisStatus(analysisId) {
    try {
      const response = await APIService.get(
        `/api/v1/scam/detector/url-safety/status/${encodeURIComponent(analysisId)}`,
      );
      return response;
    } catch (error) {
      console.error("URLSafetyAPI: Error checking analysis status:", error);
      throw error;
    }
  }

  /**
   * Analyze a URL for safety and security threats.
   * Submits the request and polls until completion.
   * @param {string} url - The URL to analyze
   * @param {string|null} context - Optional additional context from the user
   * @param {Object} options - Polling options
   * @param {number} options.pollInterval - Interval between polls in ms (default: 3000)
   * @param {number} options.maxPolls - Maximum number of polls (default: 40 = 120s)
   * @param {function} options.onStatusUpdate - Optional callback(status, pollCount)
   * @returns {Promise} Promise that resolves to the analysis result
   */
  static async analyzeURL(url, context = null, options = {}) {
    const {
      pollInterval = 3000,
      maxPolls = 40,
      onStatusUpdate = null,
    } = options;

    // Step 1: Submit the analysis (returns quickly)
    const submitResponse = await URLSafetyAPI.submitAnalysis(url, context);
    const analysisId = submitResponse.analysisId;

    if (!analysisId) {
      throw new Error("No analysisId returned from server");
    }

    console.log("URLSafetyAPI: Polling for result, analysisId:", analysisId);

    // Step 2: Poll for completion using shared utility
    return pollForResult(
      async (pollCount) => {
        const statusResponse = await URLSafetyAPI.checkAnalysisStatus(analysisId);

        if (onStatusUpdate) {
          onStatusUpdate(statusResponse.status, pollCount);
        }

        if (statusResponse.status === "COMPLETED" && statusResponse.result) {
          console.log(
            "URLSafetyAPI: Analysis completed after",
            pollCount,
            "polls",
          );
          return { done: true, result: statusResponse.result };
        }

        if (statusResponse.status === "FAILED") {
          throw new Error(
            statusResponse.message || "URL analysis failed on the server",
          );
        }

        return { done: false };
      },
      {
        interval: pollInterval,
        maxPolls,
        timeoutMessage:
          "Analysis timed out. Please try again in a moment.",
      },
    );
  }
}

export default URLSafetyAPI;
